/**
 * Product Recommendations Section
 * Lazy loading pattern: appends recommendations to existing collection products
 * Handles horizontal scroll with progress bar
 */

(function () {
  'use strict';

  function initProductRecommendations() {
    const recommendationsSection = document.querySelector('.product-recommendations');
    if (!recommendationsSection) {
      return;
    }

    const url = recommendationsSection.dataset.url;
    if (!url) {
      // No API URL, just initialize scroll bar
      initScrollBar();
      return;
    }

    // Log current product context for debugging
    const urlParams = new URLSearchParams(url.split('?')[1]);

    // Use Intersection Observer for lazy loading
    const observerOptions = {
      rootMargin: '0px 0px 200px 0px', // Start loading 200px before section enters viewport
      threshold: 0.1
    };

    const handleIntersection = (entries) => {
      if (!entries[0].isIntersecting) return;

      // Stop observing once we start loading
      observer.unobserve(recommendationsSection);

      // Fetch recommendations and append to existing content
      fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(text => {

          const html = document.createElement('div');
          html.innerHTML = text;
          const fetchedSection = html.querySelector('.product-recommendations');

          if (!fetchedSection) {
            console.warn('[Product Recommendations] No .product-recommendations section found in response');
            console.warn('[Product Recommendations] Full response:', text);
            return;
          }

          const grid = recommendationsSection.querySelector('.product-recommendations__grid');
          const fetchedGrid = fetchedSection.querySelector('.product-recommendations__grid');

          // Check if recommendations were actually performed by API
          if (fetchedGrid) {
            const hasProducts = fetchedGrid.querySelectorAll('.product-card').length > 0;
            if (!hasProducts && text.length < 1000) {
              console.warn('[Product Recommendations] ⚠️ API response contains no product cards. Response preview:', text.substring(0, 500));
            }
          }

          if (!grid) {
            console.warn('[Product Recommendations] No grid found in current section');
            return;
          }

          if (!fetchedGrid) {
            console.warn('[Product Recommendations] No grid found in fetched section');
            return;
          }

          const existingProducts = grid.querySelectorAll('.product-card');
          const recommendedProducts = fetchedGrid.querySelectorAll('.product-card');

          // Track existing product IDs to avoid duplicates
          const existingProductIds = new Set();
          const existingProductHandles = [];
          existingProducts.forEach(card => {
            const productLink = card.querySelector('a[href*="/products/"]');
            if (productLink) {
              const productHandle = productLink.href.match(/\/products\/([^/?]+)/)?.[1];
              if (productHandle) {
                existingProductIds.add(productHandle);
                existingProductHandles.push(productHandle);
              }
            }
          });

          // Append only new recommended products that aren't already displayed
          let appendedCount = 0;
          const appendedHandles = [];
          recommendedProducts.forEach(card => {
            const productLink = card.querySelector('a[href*="/products/"]');
            if (productLink) {
              const productHandle = productLink.href.match(/\/products\/([^/?]+)/)?.[1];
              if (productHandle && !existingProductIds.has(productHandle)) {
                // Clone the card to avoid DOM issues
                const clonedCard = card.cloneNode(true);
                grid.appendChild(clonedCard);
                existingProductIds.add(productHandle);
                appendedHandles.push(productHandle);
                appendedCount++;
              }
            }
          });

          // Add visual indicator if recommendations were loaded
          if (appendedCount > 0) {
            // Add a data attribute to indicate recommendations were loaded
            recommendationsSection.setAttribute('data-recommendations-loaded', 'true');
          } else if (recommendedProducts.length > 0) {
            console.warn('[Product Recommendations] ⚠️ API returned', recommendedProducts.length, 'products but all were duplicates of existing products');
          } else {
            console.warn('[Product Recommendations] ⚠️ API returned no products. Check if recommendations are configured or if store has enough purchase history.');
          }

          // Update scroll bar after appending new products
          if (appendedCount > 0) {
            // Trigger a resize event to update scroll calculations
            setTimeout(() => {
              window.dispatchEvent(new Event('resize'));
              // Update progress bar if already initialized
              const existingContainer = grid.closest('.product-recommendations')?.querySelector('.js-product-recommendations-scroll');
              if (existingContainer && existingContainer._updateProgressBar) {
                existingContainer._updateProgressBar();
              } else {
                initScrollBar();
              }
            }, 100);
          } else {
            console.warn('[Product Recommendations] No new products to append. All recommendations may already be displayed, or API returned no products.');
          }

          initScrollBar();
        })
        .catch(e => {
          console.error('[Product Recommendations] Error loading product recommendations:', e);
          console.error('[Product Recommendations] URL was:', url);
          initScrollBar();
        });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);
    observer.observe(recommendationsSection);

    // Initialize scroll bar immediately for existing content
    initScrollBar();
  }

  // Track if scroll bar is initialized to prevent duplicate listeners
  let scrollBarInitialized = false;

  function initScrollBar() {
    const scrollContainer = document.querySelector('.js-product-recommendations-scroll');
    const progressBar = document.querySelector('.product-recommendations__scroll-bar-thumb');
    const scrollBarWrapper = document.querySelector('.product-recommendations__scroll-bar');

    if (!scrollContainer || !progressBar || !scrollBarWrapper) return;

    // Prevent duplicate initialization
    if (scrollBarInitialized) {
      // Just update the progress bar position if already initialized
      const existingContainer = document.querySelector('.js-product-recommendations-scroll');
      if (existingContainer && existingContainer._updateProgressBar) {
        existingContainer._updateProgressBar();
      }
      return;
    }

    scrollBarInitialized = true;

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    function updateProgressBar() {
      const scrollLeft = scrollContainer.scrollLeft;
      const scrollWidth = scrollContainer.scrollWidth;
      const clientWidth = scrollContainer.clientWidth;
      const maxScroll = scrollWidth - clientWidth;
      const trackElement = scrollContainer.closest('.product-recommendations')?.querySelector('.product-recommendations__scroll-bar-track');

      if (!trackElement) return;

      const trackWidth = trackElement.clientWidth;

      // Hide scrollbar if content doesn't overflow
      if (maxScroll <= 0) {
        scrollBarWrapper.style.display = 'none';
        return;
      } else {
        scrollBarWrapper.style.display = 'block';
      }

      const thumbWidth = (clientWidth / scrollWidth) * trackWidth;
      const maxThumbPosition = trackWidth - thumbWidth;
      const scrollPosition = scrollLeft / maxScroll;
      const thumbPosition = scrollPosition * maxThumbPosition;

      progressBar.style.width = `${thumbWidth}px`;
      progressBar.style.left = `${thumbPosition}px`;
    }

    // Draggable thumb functionality
    function onThumbMouseDown(e) {
      isDragging = true;
      startX = e.clientX || e.touches?.[0]?.clientX || 0;
      startScrollLeft = scrollContainer.scrollLeft;

      // Remove smooth scroll for instant response
      scrollContainer.style.scrollBehavior = 'auto';
      document.body.style.userSelect = 'none';

      e.preventDefault();
    }

    function onMouseMove(e) {
      if (!isDragging) return;

      const x = e.clientX || e.touches?.[0]?.clientX || 0;
      const deltaX = x - startX;
      const trackElement = scrollContainer.closest('.product-recommendations')?.querySelector('.product-recommendations__scroll-bar-track');

      if (!trackElement) return;

      const trackWidth = trackElement.clientWidth;
      const scrollWidth = scrollContainer.scrollWidth;
      const clientWidth = scrollContainer.clientWidth;
      const thumbWidth = (clientWidth / scrollWidth) * trackWidth;
      const maxThumbPosition = trackWidth - thumbWidth;
      const maxScroll = scrollWidth - clientWidth;

      // Calculate scroll position based on thumb drag
      const scrollDelta = (deltaX / maxThumbPosition) * maxScroll;
      scrollContainer.scrollLeft = startScrollLeft + scrollDelta;

      e.preventDefault();
    }

    function onMouseUp() {
      if (!isDragging) return;

      isDragging = false;
      // Restore smooth scroll
      scrollContainer.style.scrollBehavior = 'smooth';
      document.body.style.userSelect = '';
    }

    // Mouse events
    progressBar.addEventListener('mousedown', onThumbMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    // Touch events for mobile
    progressBar.addEventListener('touchstart', onThumbMouseDown, { passive: false });
    document.addEventListener('touchmove', onMouseMove, { passive: false });
    document.addEventListener('touchend', onMouseUp);

    scrollContainer.addEventListener('scroll', updateProgressBar);
    window.addEventListener('resize', updateProgressBar);
    updateProgressBar();

    // Expose updateProgressBar for external calls
    scrollContainer._updateProgressBar = updateProgressBar;
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductRecommendations);
  } else {
    initProductRecommendations();
  }

  // Re-initialize on section load (Theme Editor support)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      if (event.target.querySelector('.product-recommendations')) {
        initProductRecommendations();
      }
    });
  }
})();
